import { FetchPolicy } from 'apollo-client'
import React from 'react'
import { useQuery } from 'react-apollo'

const filterOutDuplicates = (arr: { id: string }[]) => {
  const ids: any = {}
  return arr.filter(item => {
    if (!item.id) {
      return true
    }
    return ids[item.id] ? false : (ids[item.id] = true)
  })
}

interface PaginatingProps {
  fetchPolicy?: FetchPolicy
  forceRefetch?: boolean
  lookupProp: string
  query: any
  variables?: any
}

// https://github.com/apollographql/apollo-client/blob/master/packages/apollo-client/src/core/networkStatus.ts
// 1: loading
// 2: setVariables
// 3: fetchMore
// 4: refetch
// 5: nothing in the docs
// 6: poll
// 7: no request in flight and no errors
// 8: no request in flight and errors
export const usePagingSearchQuery = ({
  fetchPolicy,
  forceRefetch,
  lookupProp,
  query,
  variables,
}: PaginatingProps) => {
  const options: any = {
    fetchPolicy: fetchPolicy || 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables,
  }
  const { data, error, fetchMore, networkStatus, refetch } = useQuery(
    query,
    options,
  )
  React.useEffect(() => {
    if (forceRefetch && refetch) {
      refetch(variables)
    }
  })
  const showInitialLoader = networkStatus === 1 || networkStatus === 2
  const showPageLoader = networkStatus === 3
  const newPage = !showInitialLoader && data && data[lookupProp]
  const newData = newPage && newPage.data
  const newLinks = newPage && newPage.links
  const nextPage = parseInt(newLinks && newLinks.next, 10)
  const hasData = newData && newData.length > 0
  const showZeroState = networkStatus === 7 && !hasData
  const reachedLastPage = isNaN(nextPage)
  const loadNextPage =
    nextPage &&
    (() =>
      fetchMore({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult[lookupProp]) {
            return prev
          }
          return {
            [lookupProp]: {
              ...prev[lookupProp],
              data: filterOutDuplicates([
                ...prev[lookupProp].data,
                ...fetchMoreResult[lookupProp].data,
              ]),
              links: fetchMoreResult[lookupProp].links,
            },
          }
        },
        variables: {
          ...variables,
          page: nextPage,
        },
      }))

  return {
    data: newData,
    error,
    loadNextPage,
    loading: showInitialLoader || showPageLoader,
    reachedLastPage,
    showInitialLoader,
    showPageLoader,
    showZeroState,
  }
}
